import { useNotification } from '@kyvg/vue3-notification';
import { AddressEndpointDTO, AddressDTO } from '~/restAPI/data-contracts';
import { useApi } from '~/restAPI';
import { Account } from '~/restAPI/Account';
import { Address } from '~/restAPI/Address';
import { useAccountService } from '~/providers/account.js';

import type { GenericFormValues } from '~/types/components/form';

export interface IAddressForm extends GenericFormValues {
  lastName: string;
  firstName: string;
  patronymicName?: string;
  phone: string;
  region?: string;
  city: AddressDTO;
  street: string;
  building: string;
  flat?: string;
}

interface IAddressFormRequest {
  lastName: string;
  firstName: string;
  patronymicName?: string;
  phone: string;
  region?: string;
  city: string;
  street: string;
  building: string;
  flat?: string;
}

export function useAddresses() {
  const addresses = useState<AddressEndpointDTO[]>('addresses', () => ([]));
  const currentAddress = useState<AddressEndpointDTO | undefined | null>('useAddressesCurrent', () => undefined);

  const cancelGetCityToken = 'cancelGetCityToken';

  const { notify } = useNotification();
  const { createApiInstance } = useApi();
  const accountApi = createApiInstance(Account);
  const addressApi = createApiInstance(Address);
  const { deleteAddressEndpoint, saveAddressEndpoint } = useAccountService();

  const keyPrefix = {
    street: 'ул. ',
    building: 'д. ',
    flat: 'кв. ',
  };

  const {
    execute: getAddresses,
  } = useLazyAsyncData(() => accountApi.v2AccountAddressendpointsList({ includedDeliveryCost: true }, { format: 'json' })
    .then((data) => {
      addresses.value = data.data.data ?? [];
      return addresses;
    })
    .catch((error) => {
      notify({
        group: 'mainNotifications',
        type: 'error',
        text: error?.data?.message,
      });
      throw error;
    }), { immediate: false });

  function getNameText(address: AddressEndpointDTO) {
    return [
      address.firstName,
      address.patronymicName,
      address.lastName,
    ].filter((data) => !!data).join(' ');
  }

  function getAddressText(address: AddressEndpointDTO) {
    return [
      address.address?.country,
      address.address?.region,
      address.address?.city,
      address.address?.address,
    ].filter((data) => !!data).join(', ');
  }

  function saveAddress(savedAddress: AddressEndpointDTO) {
    if (currentAddress.value?.id === savedAddress.id) {
      editAddressEndpoint(savedAddress);
      return;
    }

    addAddressEndpoint(savedAddress);
  }

  // eslint-disable-next-line consistent-return
  async function saveAddressRequest(values: IAddressForm) {
    try {
      const address = compileAddressEndpoint(values, currentAddress.value);
      const result = await saveAddressEndpoint(address);

      if (result) {
        saveAddress(result);
      }

      return result;
    } catch (e) {
      console.error(e);
    }
  }

  function addAddressEndpoint(address: AddressEndpointDTO) {
    addresses.value.push(address);
  }

  function editAddressEndpoint(address: AddressEndpointDTO) {
    addresses.value = addresses.value.map((a) => (a.id === address.id ? address : a));
  }

  function removeAddressEndpoint(addressId: number) {
    addresses.value = addresses.value.filter((a) => a.id !== addressId);
  }

  function getAddressFields(addressData?: AddressEndpointDTO | null) {
    const result = {
      street: '',
      building: '',
      flat: '',
    };
    let address = addressData?.address?.address ?? '';

    if (!addressData || !address) return result;

    (Object.keys(keyPrefix) as ('street' | 'building' | 'flat')[])
      .reverse()
      .forEach((key) => {
        const addrSplitted = address.split(keyPrefix[key]);

        if (addrSplitted.length > 1) {
          result[key] = addrSplitted[1].replaceAll(',', '').trim();
          address = addrSplitted[0];
        }
      });

    return result;
  }

  function getFullAddress(vals: IAddressForm) {
    return (Object.keys(keyPrefix) as ('street' | 'building' | 'flat')[])
      .map((key) => (vals[key] ? `${keyPrefix[key]} ${vals[key]!.replaceAll(',', ' ').trim()}` : ''))
      .filter((v) => !!v).join(', ');
  }

  function compileAddressEndpoint(vals: IAddressForm, addressData: AddressEndpointDTO | null | undefined) {
    const {
      lastName, firstName, patronymicName, phone,
    } = vals;
    const phoneFormatted = phone!.replace(/[^0-9+]+/g, '');
    const address:AddressEndpointDTO = {
      lastName,
      firstName,
      patronymicName,
      phone: phoneFormatted,
      address: {
        ...vals.city,
        address: getFullAddress(vals),
      },
    };

    if (addressData) {
      address.id = addressData.id;
    }

    return address;
  }

  function getBigCity() {
    return addressApi.getBigCitiesUsingGet({ format: 'json' })
      .then((data) => data.data.data);
  }

  function getCity(str: string) {
    return addressApi.getCitiesUsingGet({ str, isDaDataEnabled: true }, {
      format: 'json',
      cancelToken: cancelGetCityToken,
    })
      .then((data) => data.data.data);
  }

  // eslint-disable-next-line consistent-return
  async function removeAddressRequest(addressId: number) {
    try {
      const result = await deleteAddressEndpoint(addressId);

      if (result) {
        removeAddressEndpoint(addressId);
      }

      return result;
    } catch (e) {
      console.error(e);
    }
  }

  return {
    addressApi,
    currentAddress,
    addresses,
    getNameText,
    getAddresses,
    getAddressText,
    saveAddress,
    getAddressFields,
    getBigCity,
    getCity,
    addAddressEndpoint,
    editAddressEndpoint,
    removeAddressEndpoint,
    compileAddressEndpoint,
    removeAddressRequest,
    saveAddressRequest,
    cancelGetCityToken,
  };
}
